import React, { useState } from "react";
import {
  SiteHeader, SiteFooter,
} from "../components/dnh/SiteChrome";
import { AdminSidebar, AdminHero, AdminProvider } from "../components/dnh/admin/AdminShell";
import { useAuth } from "../components/dnh/AuthContext";
import Overview from "../components/dnh/admin/Overview";
import ReviewQueue from "../components/dnh/admin/ReviewQueue";
import Clusters from "../components/dnh/admin/Clusters";
import Sources from "../components/dnh/admin/Sources";
import Topics from "../components/dnh/admin/Topics";
import Users from "../components/dnh/admin/Users";
import People from "../components/dnh/admin/People";
import SupportDesk from "../components/dnh/admin/SupportDesk";
import DuplicateReports from "../components/dnh/admin/DuplicateReports";
import ContentEconomics from "../components/dnh/admin/ContentEconomics";
import LLMObservability from "../components/dnh/admin/LLMObservability";
import DataPipeline from "../components/dnh/admin/DataPipeline";
import SystemHealth from "../components/dnh/admin/SystemHealth";
import FeedHealth from "../components/dnh/admin/FeedHealth";
import { EditorialDesk } from "../components/dnh/admin/perspectives/EditorialDesk";
import { SearchQuality } from "../components/dnh/admin/SearchQuality";

/* ============================================================
   DNH — Admin Dashboard (/admin)
   Left-sidebar layout: sticky nav + scrollable content.
   BreadcrumbProvider gives sub-components a way to push trail items
   into AdminHero without prop drilling.
   ============================================================ */

export default function Admin() {
  const { user } = useAuth();
  const [active, setActive] = useState("Overview");
  const [sourceInitSearch, setSourceInitSearch] = useState("");

  const navigateTo = (tab, search = "") => {
    setActive(tab);
    if (search) setSourceInitSearch(search);
  };

  const headerUser = user ? {
    name: user.name || user.email || "Admin",
    initials: (user.name || user.email || "A").slice(0, 2).toUpperCase(),
    email: user.email,
    avatar_url: user.avatar_url || user.picture || null,
    subscription_tier: user.subscription_tier || "super_admin",
    is_admin: true,
    plan: "super_admin",
  } : { name: "Editor-in-Chief", initials: "EC", plan: "Pro", is_admin: true };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SiteHeader activeNav="" user={headerUser} ticker />

      {/* BreadcrumbProvider scopes the sub-trail to the admin area */}
      <AdminProvider activeSection={active}>
        <div className="flex items-start">

          {/* Left sidebar (desktop) + mobile bar/drawer */}
          <AdminSidebar active={active} setActive={navigateTo} />

          {/* Right content area */}
          <main className="flex-1 min-w-0">
            <AdminHero active={active} onNavigate={navigateTo} />

            <div className="min-h-[60vh]">
              {active === "Overview"           && <Overview onOpenTab={setActive} />}
              {active === "Feed Health"        && <FeedHealth />}
              {active === "Perspectives"       && <EditorialDesk />}
              {active === "Search Quality"     && <SearchQuality onNavigateTo={navigateTo} />}
              {active === "Review Queue"       && <ReviewQueue />}
              {active === "Clusters"           && <Clusters />}
              {active === "Sources"            && (
                <Sources
                  initialSearch={sourceInitSearch}
                  onInitSearchConsumed={() => setSourceInitSearch("")}
                />
              )}
              {active === "Topics"             && <Topics />}
              {active === "Users"              && <Users />}
              {active === "People"            && <People />}
              {active === "Support"            && <SupportDesk />}
              {active === "Duplicate Reports"  && <DuplicateReports />}
              {active === "Content Economics"  && <ContentEconomics />}
              {active === "Observability"      && <LLMObservability />}
              {active === "Data Pipeline"      && <DataPipeline />}
              {active === "System Health"      && <SystemHealth />}
            </div>
          </main>
        </div>
      </AdminProvider>

      <SiteFooter
        variant="topics"
        topicName={`Admin · ${active}`}
        sectionsOverride={[{ label: active, target: '[data-testid="admin-page-title"]' }]}
      />
    </div>
  );
}
